/**
 * Multi-unit Small UPS Summary display (#1154 / #996) — descriptor-driven
 * per-instance rows via Story 4.5 migration.
 *
 * Story 4.5 (#997) re-points this shim at the descriptor Summary source
 * (`smallUpsDescriptorSummary.ts`) so the Summary surface flows through the
 * shared engine at byte-identical parity with the pre-refactor bespoke output
 * (captured golden masters are the oracle). The section-agnostic helpers
 * (`resolveSummarySmallUpsConfigurations`, `countFilledSmallUpsSummaryFields*`)
 * stay bespoke; only the row-set + per-unit value + per-unit title accessors
 * delegate to the descriptor source.
 */

import {
  SMALL_UPS_EDITOR_SUBSECTION_FIELDS,
  SMALL_UPS_PER_INSTANCE_EDITOR_FIELD_KEYS,
} from '../request/fields/requestFieldRegistry';
import {
  getSmallUpsDescriptorSummaryDefinitions,
  getSmallUpsDescriptorUnitTitle,
  smallUpsDescriptorUnitRowValue,
} from '../request/sections/smallUpsDescriptorSummary';
import { countSubsectionFields } from '../utils/subsectionHeaderMetrics';

import type { SummaryCardRow } from './summaryCardSourceTypes';
import type { SmallUpsConfiguration } from '../types/smallUps';

export {
  SMALL_UPS_EDITOR_SUBSECTION_FIELDS,
  SMALL_UPS_PER_INSTANCE_EDITOR_FIELD_KEYS as SMALL_UPS_PER_INSTANCE_FIELD_KEYS,
};

/**
 * Full ordered per-unit Small UPS Summary row set. Delegates to the
 * descriptor-driven Summary source (`smallUpsDescriptorSummary.ts`).
 */
export function getPerSmallUpsSummaryDefinitions(): SummaryCardRow[] {
  return getSmallUpsDescriptorSummaryDefinitions();
}

export function resolveSummarySmallUpsConfigurations(input: {
  sessionUnits?: SmallUpsConfiguration[] | null;
  draftUnits?: SmallUpsConfiguration[] | null;
  persistedUnits?: SmallUpsConfiguration[] | null;
  /** #997 C3: true when edited this session — an empty edited array is authoritative (no delete-all resurrection). */
  edited?: boolean;
}): SmallUpsConfiguration[] {
  const { sessionUnits, draftUnits, persistedUnits, edited } = input;
  if (sessionUnits && sessionUnits.length > 0) {
    return sessionUnits;
  }
  if (edited) {
    return draftUnits ?? [];
  }
  if (draftUnits && draftUnits.length > 0) {
    return draftUnits;
  }
  if (persistedUnits && persistedUnits.length > 0) {
    return persistedUnits;
  }
  return [];
}

/**
 * Per-unit Summary value — delegates to the descriptor-driven Summary source.
 */
export function smallUpsSummaryUnitRowValue(
  unit: SmallUpsConfiguration,
  dataField: string,
): string | number | boolean | null | undefined {
  return smallUpsDescriptorUnitRowValue(unit, dataField);
}

/**
 * Per-unit Summary card title — `Existing/New — <name>` (fallback
 * `Existing/New Small UPS N`). Delegates to the descriptor-driven title
 * function so the Summary shim and the descriptor source stay aligned
 * (mirrors LU / PD shim additions).
 */
export function getSmallUpsSummaryUnitTitle(
  unit: SmallUpsConfiguration,
  index: number,
): string {
  return getSmallUpsDescriptorUnitTitle(unit, index);
}

export function countFilledSmallUpsSummaryFields(
  unit: SmallUpsConfiguration,
  rows?: readonly SummaryCardRow[],
): number {
  const fieldKeys = new Set(
    (rows ?? getPerSmallUpsSummaryDefinitions()).map(row => row.dataField),
  );
  const fields = SMALL_UPS_EDITOR_SUBSECTION_FIELDS.filter(field =>
    fieldKeys.has(field.key),
  );
  return countSubsectionFields(
    fields,
    unit as unknown as Record<string, unknown>,
  ).filled;
}

export function countFilledSmallUpsSummaryFieldsForUnits(
  units: readonly SmallUpsConfiguration[],
  rows: readonly SummaryCardRow[],
): number {
  return units.reduce(
    (sum, unit) => sum + countFilledSmallUpsSummaryFields(unit, rows),
    0,
  );
}
